import { ensureBridgeAgentPathInProcessEnv } from '../agents/acp/clients/bridge-agent-path.js';
import { getBridgeRoot } from '../files/cwd/index.js';
import { startFileIndexWatcher } from '../files/watch-file-index.js';
import { requestCliImmediateShutdown } from '../runtime/cli-process-interrupt.js';
import type { CreateBridgeConnectionOptions, BridgeConnectionHandle } from './bridge-connection-types.js';
import { closeBridgeConnection } from './close-bridge-connection.js';
import { createBridgeConnectionRuntime } from './create-bridge-connection-runtime.js';
import { createMainBridgeReconnectState } from './create-main-bridge-reconnect-state.js';
import { ensureBridgeConnectionDatabases } from './ensure-bridge-connection-databases.js';
import { createMainBridgeWebSocketLifecycle } from './main-bridge-ws-lifecycle.js';
import { log } from '../log.js';

export type { CreateBridgeConnectionOptions, BridgeConnectionHandle } from './bridge-connection-types.js';

/**
 * Create the permanent WebSocket bridge connection (with workspaceId + token).
 * Sets up ACP client (from bridge-registered agent types), and handles prompt/skill_call/proxy/cli_ping.
 * Reconnects on disconnect (with backoff) until close() is called or auth becomes invalid.
 */
export async function createBridgeConnection(
  options: CreateBridgeConnectionOptions,
): Promise<BridgeConnectionHandle> {
  const { apiUrl, workspaceId, justAuthenticated, onAuthInvalid, persistTokens } = options;
  const logFn = options.log ?? log;

  ensureBridgeAgentPathInProcessEnv();
  await ensureBridgeConnectionDatabases(logFn);

  const { state, getWs } = createMainBridgeReconnectState();
  const runtime = await createBridgeConnectionRuntime(options, { state, getWs, logFn });

  const { connect } = createMainBridgeWebSocketLifecycle({
    state: runtime.state,
    getWs: runtime.getWs,
    apiUrl,
    workspaceId,
    justAuthenticated: justAuthenticated === true,
    logFn: runtime.logFn,
    messageDeps: runtime.messageDeps,
    tokens: runtime.tokens,
    persistTokens,
    onAuthInvalid,
    e2ee: runtime.e2ee,
    identifyReportedPaths: runtime.identifyReportedPaths,
    bridgeHeartbeat: runtime.bridgeHeartbeat,
    onBridgeSocketOpen: runtime.scheduleInitialIndexBuildsOnce,
  });

  connect();

  const stopFileIndexWatcher = startFileIndexWatcher(getBridgeRoot());
  const bridgeAccess = await runtime.bridgeAccessPromise;
  const localShortcut = await runtime.localShortcutPromise;

  return {
    close: async () => {
      requestCliImmediateShutdown();
      stopFileIndexWatcher();
      runtime.bridgeHeartbeat.stop();
      await closeBridgeConnection(
        runtime.state,
        runtime.acpManager,
        runtime.previewEnvironmentManager,
        runtime.logFn,
      );
      await bridgeAccess.close();
      await localShortcut.close();
    },
  };
}
